import { api, endpoints } from "api/api";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";

// === Types ===

export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'span' | 'p';

export interface ShopApi {
  id: number;
  name: string;
  hostname?: string | null;
  description: string;
}

export interface LandingPageApi {
  id: number;
  name: string;
  slug: string;
  isPublished: boolean;
  order: number;
  shopId: number;
  createdDate: Date;
  lastModifiedDate?: Date;
  sections?: LandingPageSectionApi[];
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  categoryId?: number | null;
  categoryName?: string | null;
}

export interface LandingPageSectionApi {
  id: number;
  type: string;
  name?: string;
  anchorId?: string | null;
  content: unknown;
  margins?: SectionMargins;
  order: number;
  // absent on blog post sections (they carry blogPostId instead)
  shopLandingPageId?: number;
  createdDate: Date;
  lastModifiedDate?: Date;
}

export interface CourseGroupListContent {
  name: string;
  description: string;
  button: {
    text: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  };
  courseGroupIds: number[];
  cardCustomization?: CardCustomization;
}

export interface SimpleWysiwygContent {
  animation: string;
  header: { text: string; align?: "left" | "center" | "right"; size?: "sm" | "md" | "lg" | "xl"; tag?: HeadingTag };
  content: { data: string };
  contentAttributeId?: number;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface TwoColumnsWysiwygContent {
  animation: string;
  header: { text: string; align?: "left" | "center" | "right"; size?: "sm" | "md" | "lg" | "xl"; tag?: HeadingTag };
  leftColumn: { data: string };
  rightColumn: { data: string };
  leftColumnAttributeId?: number;
  rightColumnAttributeId?: number;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface ImageContentSectionContent {
  animation: string;
  image: { src: string; alt: string; width?: string; height?: string; rounding?: string; objectPosition?: string };
  header: { text: string; color?: string; tag?: HeadingTag };
  content: { data: string };
  contentPadding?: string;
  bgStyle: string;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface ImagesCardsContent {
  animation: string;
  layout: string;
  hoverColor: string;
  images: Array<{
    title: string;
    titleTag?: HeadingTag;
    src: string;
    href: string;
  }>;
}

export interface LogosSectionContent {
  animation: string;
  header: { text: string; tag?: HeadingTag };
  logoHeight?: number;
  grayscale?: boolean;
  logos: Array<{
    src: string;
    alt: string;
    href?: string;
  }>;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface SimpleBgImageContent {
  animation: string;
  bgImage: { src: string; alt: string };
  header: { text: string; tag?: HeadingTag };
  layout: string;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface ClassicHeroContent {
  header: { text: string; tag?: HeadingTag };
  breadcrumbs: Array<{ label: string; href: string | null }>;
  useCategoryBreadcrumbs?: boolean;
  bgImage: { src: string; alt: string; position?: string };
}

export interface AccordionHeaderStyle {
  color: string;
  bold: boolean;
  hoverColor: string;
}

export interface AccordionItem {
  header: string;
  content: string;
}

export type DynamicSectionBlock =
  | { type: "wysiwyg"; content: string }
  | { type: "table"; content: string }
  | { type: "buttons"; buttons: Array<{ label: string; variant: string; href: string }> }
  | { type: "accordion"; items: AccordionItem[]; headerStyle: AccordionHeaderStyle };

export interface DynamicSectionContent {
  header: { text: string; tag?: HeadingTag };
  blocks: DynamicSectionBlock[];
}

export interface MainPageHeroContent {
  headerLine1: string;
  headerLine2: string;
  headerLine1Tag?: HeadingTag;
  headerLine2Tag?: HeadingTag;
  bgImages: Array<{ src: string; alt: string }>;
  buttons: Array<{ label: string; variant: "primary" | "secondary" | "outline"; href: string }>;
  tiles: Array<{ icon: string; label: string; subtitle: string }>;
}

export interface ProductsSectionFilter {
  type: "category" | "attribute";
  displayType: "tabs" | "dropdown" | "multiselect" | "switch";
  label?: string;
  defaultCategoryIds?: number[];
  attributeId?: number;
  attributeName?: string;
}

export interface CardCustomization {
  badgeAttributeIds?: number[];
  listAttributeIds?: number[];
}

export interface ProductsSectionContent {
  title: string;
  titleTag?: HeadingTag;
  description?: string;
  categoryIds: number[];
  excludedCourseGroupIds: number[];
  filters: ProductsSectionFilter[];
  cardCustomization?: CardCustomization;
}

export interface MapPoint {
  longitude: number;
  latitude: number;
  name: string;
  description: string;
}

export interface MapSectionContent {
  animation: string;
  header?: { text: string; tag?: HeadingTag };
  points: MapPoint[];
  mapCenter?: { longitude: number; latitude: number };
  mapZoom?: number;
}

export interface Opinion {
  name: string;
  opinion: string;
  source: string;
}

export interface OpinionsSectionContent {
  animation: string;
  header: { text: string; color?: string; tag?: HeadingTag };
  opinions: Opinion[];
}

export interface ContactLocationDetail {
  label: string;
  value: string;
}

export interface ContactLocationContent {
  title: string;
  titleTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span';
  addressLines: string[];
  details: ContactLocationDetail[];
}

export interface ContactLocationsSectionContent {
  animation: string;
  locations: ContactLocationContent[];
}

export interface ContactFormPerson {
  name: string;
  avatar?: string;
  phone?: string;
  email?: string;
}

export interface ContactFormArea {
  header: string;
  headerTag?: HeadingTag;
  contacts: ContactFormPerson[];
}

export interface ContactFormSectionContent {
  animation: string;
  areas: ContactFormArea[];
  buttonVariant?: string;
}

export interface ContactFormSettings {
  notificationEmails: string[];
}

export interface ContactFormSubmission {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone: string;
  message: string;
  createdDate: Date;
  isRead: boolean;
}

export interface ContactFormSubmissionsResponse {
  items: ContactFormSubmission[];
  total: number;
}

export interface PeopleSectionPerson {
  avatar: string;
  name: string;
  role: string;
  description: string;
}

export interface PeopleSectionContent {
  animation: string;
  header: { text: string; tag?: HeadingTag };
  people: PeopleSectionPerson[];
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
}

export interface CardsSectionContent {
  animation: string;
  header?: { text: string; tag?: HeadingTag };
  cards: CardItemConfig[];
  margins?: SectionMargins;
}

export interface VideoContentCardsSectionContent {
  animation: string;
  header?: { text: string; tag?: HeadingTag; align?: "left" | "center" | "right" };
  videoUrl?: string;
  videoUrlAttributeId?: number;
  content?: { data: string };
  contentAttributeId?: number;
  cards?: CardItemConfig[];
  margins?: SectionMargins;
}

export interface FooterSocial {
  image: string;
  bgColor: string;
  link: string;
}

export interface FooterTerm {
  name: string;
  link: string;
}

export interface NewsletterSettings {
  enabled?: boolean;
  title?: string;
  description?: string;
  buttonText?: string;
  successMessage?: string;
}

export interface FooterNavLink {
  name: string;
  link: string;
}

export interface FooterNavGroup {
  title?: string;
  links: FooterNavLink[];
}

export interface FooterSettings {
  logo?: string;
  bgColor?: string;
  textColor?: string;
  mainText?: string;
  logoSize?: number;
  fontFamily?: string;
  fontSize?: number;
  socials?: FooterSocial[];
  terms?: FooterTerm[];
  newsletter?: NewsletterSettings;
  navColumns?: FooterNavGroup[];
}

export interface BreadcrumbSettings {
  fontFamily?: string;
}

export interface GoogleAnalyticsConfig {
  measurementId?: string;
  enabled?: boolean;
}

export interface ArchivedCoursePageSettings {
  offerPageUrl?: string;
}

export interface MetaPixelConfig {
  pixelId: string | null;
  enabled: boolean;
}

export interface NewsletterSubscription {
  id: number;
  email: string;
  createdDate: Date;
}

export interface NewsletterSubscriptionsResponse {
  items: NewsletterSubscription[];
  total: number;
}

export interface ButtonStyle {
  id: number;
  name: string;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor?: string;
  hoverBgColor?: string;
  hoverTextColor?: string;
}

export interface HeadingStyles {
  fontSizeMobile: string;
  fontSizeDesktop: string;
  fontWeight: string;
  color: string;
  alignment: "left" | "center" | "right";
  marginBottom: string;
  showUnderline: boolean;
  underlineColor: string;
}

// Menu Config Types
export interface MenuStyleConfig {
  height: number;
  logo?: string;
  backgroundColor: string;
  textColor: string;
  submenuBgColor: string;
  submenuTextColor: string;
  submenuTextColorHover: string;
}

export interface SubmenuItem {
  id: string;
  label: string;
  href: string;
  order: number;
}

export interface MenuItem {
  id: string;
  label: string;
  href: string;
  external?: boolean;
  order: number;
  submenu?: SubmenuItem[];
}

export interface MenuConfig {
  standard: MenuStyleConfig;
  sticky: MenuStyleConfig;
  items: MenuItem[];
}

// Course Detail Page Config Types
export interface CourseHeroContent {
  showBreadcrumbs?: boolean;
  showTitle?: boolean;
  imageHeight?: number;
  backgroundMode?: "course-image" | "color" | "image";
  bgColor?: string;
  bgImage?: string;
  breadcrumbAttributeId?: number;
  showCourseNameInBreadcrumbs?: boolean;
}

export interface CourseEnrollmentContent {
  variant: "mobile-inline" | "desktop-sidebar";
  title?: string;
  titleTag?: HeadingTag;
  displayMode?: "default" | "card";
  showCourseGroupName?: boolean;
  showImage?: boolean;
  showCourseDates?: boolean;
  showPrice?: boolean;
  mobileSticky?: boolean;
  showBorder?: boolean;
  asideMarginTop?: number;
}

export interface TrainersTabSettings {
  maxVisible?: number;
  compactMode?: boolean;
}

export interface ScheduleTabSettings {
  variant?: "carousel" | "accordion" | "simple-list";
}

export interface ProgramTabSettings {
  programVariant?: "accordion" | "modal" | "static-list";
}

export type BuiltInTabSettings = TrainersTabSettings & ScheduleTabSettings & ProgramTabSettings;

export interface BuiltInTabConfig {
  kind: "built-in";
  id: string;
  label: string;
  description?: string;
  descriptionAttributeId?: number;
  descriptionBelow?: string;
  descriptionBelowAttributeId?: number;
  settings?: BuiltInTabSettings;
}

export interface CustomTabConfig {
  kind: "custom";
  id: string;
  label: string;
  description?: string;
  descriptionAttributeId?: number;
  descriptionBelow?: string;
  descriptionBelowAttributeId?: number;
  content: string;
}

export interface CardItemConfig {
  header: string;
  headerTag?: HeadingTag;
  content: string;
  bgColor: string;
  textColor: string;
  attributeId?: number;
}

export interface CardsTabConfig {
  kind: "cards";
  id: string;
  label: string;
  description?: string;
  descriptionAttributeId?: number;
  descriptionBelow?: string;
  descriptionBelowAttributeId?: number;
  cards: CardItemConfig[];
}

export type TabConfig = BuiltInTabConfig | CustomTabConfig | CardsTabConfig;

export interface CourseTabsContent {
  tabs: TabConfig[];
  variant: "scroll" | "tabs";
  showSectionHeaders?: boolean;
  tabsLayout?: "wrap" | "scroll";
}

export interface CourseStickyBarContent {
  buttonText?: string;
  anchorId?: string;
}

export interface MobileStickyCtaContent {
  buttonText: string;
  anchorId?: string;
  subtitleText?: string;
  buttonVariant?: string;
}

export interface StaticPdfContent {
  pdfUrl: string;
  title?: string;
}

export interface VideoSectionContent {
  videoUrl: string;
  videoUrlAttributeId?: number;
  header?: {
    text: string;
    tag?: HeadingTag;
    align?: "left" | "center" | "right";
  };
  description?: string;
  descriptionAttributeId?: number;
  descriptionPosition?: "top" | "bottom" | "left" | "right";
}

export interface BlogQuoteContent {
  quote: string;
  author?: string;
  source?: string;
  sourceUrl?: string;
  variant?: "default" | "highlight";
}

export interface CourseDetailPageSectionConfig {
  type: string;
  name?: string;
  content: unknown;
  order: number;
}

// V2 Layout-based config types
export interface ColumnWidth {
  value: number;
  unit: "auto" | "fr" | "px" | "%";
}

export interface ColumnMobileConfig {
  visible: boolean;
  order?: number;
}

export interface ColumnMargin {
  default?: { left?: number; right?: number };
  md?: { left?: number; right?: number };
  lg?: { left?: number; right?: number };
}

export interface LayoutColumn {
  id: string;
  width: ColumnWidth;
  mobileConfig: ColumnMobileConfig;
  margin?: ColumnMargin;
  sections: LayoutSectionConfig[];
}

export interface LayoutSectionConfig {
  type: string;
  name?: string;
  content: unknown;
}

export interface LayoutRow {
  id: string;
  columns: LayoutColumn[];
  gap?: number;
  marginTop?: number;
  marginBottom?: number;
}

export interface CourseDetailPageConfig {
  rows: LayoutRow[];
}

// SEO Settings
export interface SeoSettings {
  organizationName: string;
  alternateName: string;
  siteName: string;
  logoUrl: string;
  siteDescription: string;
  googleSiteVerificationCode?: string;
}

// Product Card Settings
export interface ProductCardSettings {
  badgeAttributeIds?: number[];
  listAttributeIds?: number[];
  showImage?: boolean;
  showCourseGroupName?: boolean;
}

// Section Margins Types
export interface SectionMarginValue {
  mobile: number;
  desktop: number;
  xl?: number;
}

export type ContainerWidth = "default" | "narrow" | "medium" | "wide" | "full";

export interface SectionMargins {
  marginTop: SectionMarginValue;
  marginBottom: SectionMarginValue;
  containerWidth?: ContainerWidth;
}

export const DEFAULT_SECTION_MARGINS: SectionMargins = {
  marginTop: { mobile: 64, desktop: 96, xl: 96 },
  marginBottom: { mobile: 64, desktop: 96, xl: 96 },
};

// === Form Types ===

export interface ShopFormValues {
  name: string;
  description: string;
}

export interface LandingPageFormValues {
  name: string;
  slug?: string;
  isPublished: boolean;
  order?: number;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  categoryId?: number | null;
}

export interface SectionFormValues {
  type: string;
  name?: string;
  anchorId?: string;
  content: unknown;
  margins?: SectionMargins;
  order?: number;
}

// === API Functions ===

// Shop (singleton)
const fetchShop = (): AxiosPromise<ShopApi> => api.get<ShopApi>(endpoints.SHOP.GET());

const updateShop = (data: ShopFormValues): AxiosPromise<void> => api.put<void>(endpoints.SHOP.UPDATE(), data);

// Landing Pages
const fetchLandingPages = (
  skip: number,
  take: number,
  searchPhrase: string = "",
): AxiosPromise<BaseApiPaging<LandingPageApi>> =>
  api.get<BaseApiPaging<LandingPageApi>>(endpoints.SHOP.LANDING_PAGES.GET(skip, take, searchPhrase));

const fetchLandingPageById = (id: number): AxiosPromise<LandingPageApi> =>
  api.get<LandingPageApi>(endpoints.SHOP.LANDING_PAGES.GET_BY_ID(id));

const createLandingPage = (data: LandingPageFormValues): AxiosPromise<{ id: number }> =>
  api.post<{ id: number }>(endpoints.SHOP.LANDING_PAGES.CREATE(), data);

const updateLandingPage = (id: number, data: LandingPageFormValues): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.LANDING_PAGES.UPDATE(id), data);

const deleteLandingPage = (id: number): Promise<void> => api.delete(endpoints.SHOP.LANDING_PAGES.DELETE(id));

const duplicateLandingPage = (id: number): AxiosPromise<{ id: number }> =>
  api.post<{ id: number }>(endpoints.SHOP.LANDING_PAGES.DUPLICATE(id));

// Sections
const fetchSections = (landingPageId: number): AxiosPromise<LandingPageSectionApi[]> =>
  api.get<LandingPageSectionApi[]>(endpoints.SHOP.LANDING_PAGES.SECTIONS.GET(landingPageId));

const createSection = (landingPageId: number, data: SectionFormValues): AxiosPromise<number> =>
  api.post<number>(endpoints.SHOP.LANDING_PAGES.SECTIONS.CREATE(landingPageId), data);

const updateSection = (landingPageId: number, sectionId: number, data: SectionFormValues): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.LANDING_PAGES.SECTIONS.UPDATE(landingPageId, sectionId), data);

const deleteSection = (landingPageId: number, sectionId: number): Promise<void> =>
  api.delete(endpoints.SHOP.LANDING_PAGES.SECTIONS.DELETE(landingPageId, sectionId));

const reorderSections = (landingPageId: number, sectionIds: number[]): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.LANDING_PAGES.SECTIONS.REORDER(landingPageId), { sectionIds });

// Images
const uploadImage = (file: File): AxiosPromise<{ url: string }> => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post<{ url: string }>(endpoints.SHOP.IMAGES.UPLOAD(), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Footer
const fetchFooterSettings = (): AxiosPromise<FooterSettings> => api.get<FooterSettings>(endpoints.SHOP.FOOTER.GET());

const updateFooterSettings = (data: FooterSettings): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.FOOTER.UPDATE(), data);

// Breadcrumbs
const fetchBreadcrumbSettings = (): AxiosPromise<BreadcrumbSettings> =>
  api.get<BreadcrumbSettings>(endpoints.SHOP.BREADCRUMBS.GET());

const updateBreadcrumbSettings = (data: BreadcrumbSettings) =>
  api.put(endpoints.SHOP.BREADCRUMBS.UPDATE(), data);

// Google Analytics
const fetchGoogleAnalyticsConfig = (): AxiosPromise<GoogleAnalyticsConfig> =>
  api.get<GoogleAnalyticsConfig>(endpoints.SHOP.GOOGLE_ANALYTICS.GET());

const updateGoogleAnalyticsConfig = (data: GoogleAnalyticsConfig): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.GOOGLE_ANALYTICS.UPDATE(), data);

// Archived Course Page Settings
const fetchArchivedCoursePageSettings = (): AxiosPromise<ArchivedCoursePageSettings> =>
  api.get<ArchivedCoursePageSettings>(endpoints.SHOP.ARCHIVED_COURSE_PAGE_SETTINGS.GET());

const updateArchivedCoursePageSettings = (data: ArchivedCoursePageSettings): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.ARCHIVED_COURSE_PAGE_SETTINGS.UPDATE(), data);

// Meta Pixel
const fetchMetaPixelConfig = (): AxiosPromise<MetaPixelConfig> =>
  api.get<MetaPixelConfig>(endpoints.SHOP.META_PIXEL.GET());

const updateMetaPixelConfig = (data: MetaPixelConfig): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.META_PIXEL.UPDATE(), data);

// Button Styles
const fetchButtonStyles = (): AxiosPromise<ButtonStyle[]> => api.get<ButtonStyle[]>(endpoints.SHOP.BUTTON_STYLES.GET());

const updateButtonStyles = (data: ButtonStyle[]): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BUTTON_STYLES.UPDATE(), data);

// Heading Styles
const fetchHeadingStyles = (): AxiosPromise<HeadingStyles> =>
  api.get<HeadingStyles>(endpoints.SHOP.HEADING_STYLES.GET());

const updateHeadingStyles = (data: HeadingStyles): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.HEADING_STYLES.UPDATE(), data);

// Menu Config
const fetchMenuConfig = (): AxiosPromise<MenuConfig> => api.get<MenuConfig>(endpoints.SHOP.MENU.GET());

const updateMenuConfig = (data: MenuConfig): AxiosPromise<void> => api.put<void>(endpoints.SHOP.MENU.UPDATE(), data);

// Product Card Settings
const fetchProductCardSettings = (): AxiosPromise<ProductCardSettings> =>
  api.get<ProductCardSettings>(endpoints.SHOP.PRODUCT_CARD_SETTINGS.GET());

const updateProductCardSettings = (data: ProductCardSettings): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.PRODUCT_CARD_SETTINGS.UPDATE(), data);

// Course Detail Page Config
const fetchCourseDetailPageConfig = (): AxiosPromise<CourseDetailPageConfig> =>
  api.get<CourseDetailPageConfig>(endpoints.SHOP.COURSE_DETAIL_PAGE_CONFIG.GET());

const updateCourseDetailPageConfig = (data: CourseDetailPageConfig): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.COURSE_DETAIL_PAGE_CONFIG.UPDATE(), data);

// SEO Settings
const fetchSeoSettings = (): AxiosPromise<SeoSettings> => api.get<SeoSettings>(endpoints.SHOP.SEO_SETTINGS.GET());

const updateSeoSettings = (data: SeoSettings): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.SEO_SETTINGS.UPDATE(), data);

// Newsletter Subscriptions
const fetchNewsletterSubscriptions = (skip: number, take: number): AxiosPromise<NewsletterSubscriptionsResponse> =>
  api.get<NewsletterSubscriptionsResponse>(endpoints.SHOP.NEWSLETTER_SUBSCRIPTIONS.GET(skip, take));

const deleteNewsletterSubscription = (id: number): AxiosPromise<void> =>
  api.delete(endpoints.SHOP.NEWSLETTER_SUBSCRIPTIONS.DELETE(id));

// Contact Form Settings
const fetchContactFormSettings = (): AxiosPromise<ContactFormSettings> =>
  api.get<ContactFormSettings>(endpoints.SHOP.CONTACT_FORM_SETTINGS.GET());

const updateContactFormSettings = (data: ContactFormSettings): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.CONTACT_FORM_SETTINGS.UPDATE(), data);

// Contact Form Submissions
const fetchContactFormSubmissions = (skip: number, take: number): AxiosPromise<ContactFormSubmissionsResponse> =>
  api.get<ContactFormSubmissionsResponse>(endpoints.SHOP.CONTACT_FORM_SUBMISSIONS.GET(skip, take));

const deleteContactFormSubmission = (id: number): AxiosPromise<void> =>
  api.delete(endpoints.SHOP.CONTACT_FORM_SUBMISSIONS.DELETE(id));

export const shopApi = {
  fetchShop,
  updateShop,
  fetchLandingPages,
  fetchLandingPageById,
  createLandingPage,
  updateLandingPage,
  deleteLandingPage,
  duplicateLandingPage,
  fetchSections,
  createSection,
  updateSection,
  deleteSection,
  reorderSections,
  uploadImage,
  fetchFooterSettings,
  updateFooterSettings,
  fetchBreadcrumbSettings,
  updateBreadcrumbSettings,
  fetchGoogleAnalyticsConfig,
  updateGoogleAnalyticsConfig,
  fetchMetaPixelConfig,
  updateMetaPixelConfig,
  fetchButtonStyles,
  updateButtonStyles,
  fetchHeadingStyles,
  updateHeadingStyles,
  fetchMenuConfig,
  updateMenuConfig,
  fetchProductCardSettings,
  updateProductCardSettings,
  fetchCourseDetailPageConfig,
  updateCourseDetailPageConfig,
  fetchSeoSettings,
  updateSeoSettings,
  fetchNewsletterSubscriptions,
  deleteNewsletterSubscription,
  fetchContactFormSettings,
  updateContactFormSettings,
  fetchContactFormSubmissions,
  deleteContactFormSubmission,
  fetchArchivedCoursePageSettings,
  updateArchivedCoursePageSettings,
};
